<template>
  <error-boundary error-key="placingVariants">
    <e-alert v-if="isEditingMode" v-model="isShowAlert" variant="info" closable class="mb-6">
      <div>
        <div class="paragraph-m font-600 mb-1">
          Снятие выбора с Варианта размещения и сохранение изменений приведет к удалению Варианта размещения и полному
          удалению ограничений по указанному Варианту размещения.
        </div>

        <div class="text-secondary paragraph-s">
          Повторное добавление Варианта размещения доступно через перезапуск Мастера Вариантов размещения.
        </div>
      </div>
    </e-alert>

    <div class="d-flex align-center mb-6">
      <teleport :disabled="!$vuetify.display.smAndDown" defer :to="`#${tariffTabsAppendTeleport}`">
        <div class="d-flex align-center">
          <placing-variants-filters v-model="filters" />
          <e-icon
            v-if="$vuetify.display.smAndDown && !isShowAlert"
            class="link ml-4 cursor-pointer"
            name="info"
            @click="showAlert"
          />
        </div>
      </teleport>
      <v-spacer />

      <mobile-floating-bar>
        <div class="d-flex justify-end align-center">
          <template v-if="isEditingMode">
            <placing-variants-cancel @cancel="cancelEdit" />
            <placing-variants-save :submit="save" :loading="isSubmitLoading" />
            <e-icon
              v-if="!$vuetify.display.smAndDown && !isShowAlert"
              class="link ml-4 cursor-pointer"
              name="info"
              @click="showAlert"
            />
          </template>
          <operation-button
            v-else
            :operation="activeTariff?.operations?.canEditPlacingVariants"
            prepend-icon-src="editPen"
            :loading="isStartEditPlacingVariantsLoading"
            variant="primary"
            @click="startEdit"
          >
            Редактировать
          </operation-button>
        </div>
      </mobile-floating-bar>
    </div>

    <e-form v-model="isValid" ref="formRef" @submit="submit">
      <placing-variants-tables
        v-model="items"
        :filter-room-category="filters.roomCategory"
        :filter-tourist-type="filters.touristType"
        :filter-conflicted-only="filters.onlyConflicted"
        :disabled="!isEditingMode"
        :loading="isLoading"
        tariff-page
      />
    </e-form>

    <div v-if="$vuetify.display.smAndDown" class="pt-10 mb-5" />
  </error-boundary>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import type { EFormType } from '@/core/types/components/components';
import type { ArrayElementType } from '@/core/types/gengeric';
import type { PlacingVariantType, PVMPlacingVariantsItem } from '@/parts/tariffs/types/tariffs';
import OperationButton from '@/core/components/buttons/OperationButton.vue';
import MobileFloatingBar from '@/core/components/common/MobileFloatingBar.vue';
import { EAlert, EForm, EIcon } from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import { tariffTabsAppendTeleport } from '@/core/consts/teleport';
import useConfirmationDialog from '@/core/store/useConfirmationDialog';
import { deepCloneObject } from '@/core/utils/object';
import ErrorBoundary from '@/parts/errors/components/ErrorBoundary.vue';
import PlacingVariantsCancel from '@/parts/tariffs/components/PlacingVariantsCancel.vue';
import PlacingVariantsFilters from '@/parts/tariffs/components/PlacingVariantsFilters.vue';
import PlacingVariantsSave from '@/parts/tariffs/components/PlacingVariantsSave.vue';
import PlacingVariantsTables from '@/parts/tariffs/components/PlacingVariantsTables.vue';
import { useTariffsStore } from '@/parts/tariffs/store/tariffs';
import { defaultPlacingVariantsFilter } from '@/parts/tariffs/utils/tariffs';

const {
  getPlacingVariants,
  activeTariff,
  activeTariffPlacingVariants,
  startEditPlacingVariants,
  cancelEditPlacingVariants,
  isStartEditPlacingVariantsLoading,
  updatePlacingVariants,
} = useTariffsStore();
const { showConfirmationDialog } = useConfirmationDialog();

const filters = ref(defaultPlacingVariantsFilter());

const isEditingMode = ref(false);
const isShowAlert = ref(true);

const showAlert = () => {
  isShowAlert.value = true;
};

const cancelEdit = () => {
  setItems();

  isEditingMode.value = false;
};

const startEdit = async () => {
  if (!activeTariff.value) {
    return;
  }

  const result = await startEditPlacingVariants(activeTariff.value.tariffScheduleCid);

  if (result?.errors) {
    return;
  }

  setItems();

  isEditingMode.value = true;
};

const isValid = ref(false);

const { isLoading } = useFetch(async () => {
  await getPlacingVariants();
  setItems();
});

type PlacingVariant = ArrayElementType<PlacingVariantType['suggestedPlacingVariants']> & {
  isLastVariant?: boolean;
  isConflicted?: boolean;
};

const items = ref<PVMPlacingVariantsItem[]>([]);

const setItems = () => {
  if (!activeTariffPlacingVariants.value) {
    items.value = [];
    return;
  }

  const clonedPlacingVariants = deepCloneObject(activeTariffPlacingVariants.value);

  items.value = clonedPlacingVariants
    .map((placingVariant) => {
      let allPlacingVariants: PlacingVariant[] = placingVariant.conflictedPlacingVariants.map((x) => ({
        ...x,
        isConflicted: true,
      }));

      allPlacingVariants = [...placingVariant.suggestedPlacingVariants, ...allPlacingVariants];

      const placingVariants = allPlacingVariants.map((variant) => ({
        ...variant,
        isLastVariant: allPlacingVariants.length === 1 ? true : false,
      }));

      return {
        ...placingVariant,
        roomCid: placingVariant.roomCategory.cid,
        roomName: placingVariant.roomCategory.name[0].value,
        placingVariants,
        selected: placingVariants.filter((s) => s.isChecked),
        isMassOperationActive: false,
        isMassOperationPricePerRoom: false,
        massOperationMaxNoSeatQuantity: null,
      };
    })
    .filter((x) => x.placingVariants.length);
};

const isSubmitLoading = ref(false);

const formRef = ref<EFormType>();

onBeforeUnmount(() => {
  if (isEditingMode.value) {
    void cancelEditPlacingVariants();
  }
});

onBeforeRouteLeave((to, from, next) => {
  if (isEditingMode.value) {
    showConfirmationDialog({ action: next });
  } else {
    next();
  }
});

const save = async () => {
  await formRef.value?.submit();
};

const submit = async () => {
  if (!isValid.value || !activeTariff.value) {
    return;
  }

  isSubmitLoading.value = true;

  const result = await updatePlacingVariants({
    variants: {
      tariffScheduleCid: activeTariff.value.tariffScheduleCid,
      roomPlacingVariants: items.value.map((item) => ({
        roomCategoryCid: item.roomCid,
        variants: item.selected.map((placingVariant) => ({
          roomPlacingCID: placingVariant.tarification!.roomPlacingCid,
          shortName: placingVariant.name,
          defaultAvailable: placingVariant.tarification!.defaultAvailable,
          pricePerRoom: placingVariant.tarification!.pricePerRoom,
          isNeedConfirm: placingVariant.tarification!.isNeedConfirm,
          maxNoSeatQuantity: placingVariant.tarification!.maxNoSeatQuantity,
        })),
      })),
    },
  });

  if (!result?.errors) {
    setItems();
    isEditingMode.value = false;
  }

  isSubmitLoading.value = false;
};
</script>

<style scoped lang="scss"></style>
